import { IfAbility, type SessionService } from '@efdigital/ll-addon-auth';
import { selectValue } from '@efdigital/ll-addon-ui';
import { createImpersonationLink } from '@efdigital/ll-admin-v2/data/builders/impersonation/client';
import type {
  ImpersonationLinkCreateResponse,
  ImpersonationTargetApp,
} from '@efdigital/ll-cdk-auth-impersonation-api-types';
import { uniqueId } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BsModal from 'ember-bootstrap/components/bs-modal';
import { cell, resource, use } from 'ember-resources';

interface Signature {
  Args: {
    targetUserId: string;
    targetUserName: string;
    targetApp?: ImpersonationTargetApp;
  };
  Blocks: {
    trigger: [showModal: () => void];
  };
}

const TargetApps: Record<ImpersonationTargetApp, string> = {
  'xceed-v2': 'Xceed v2',
  xceed: 'Xceed v1',
};

const TargetAppOptions = Object.entries(TargetApps).map(([value, label]) => ({
  value,
  label,
}));

export class ImpersonateUserModal extends Component<Signature> {
  @service declare session: SessionService;

  @tracked modalOpen = false;
  @tracked reason = '';
  @tracked targetApp: ImpersonationTargetApp = this.args.targetApp ?? 'xceed';
  @tracked generatedLink: ImpersonationLinkCreateResponse | null = null;
  @tracked errorMessage: string | null = null;
  @tracked isGenerating = false;
  @tracked isCopied = false;

  resetForm = (): void => {
    this.reason = '';
    this.targetApp = this.args.targetApp ?? 'xceed-v2';
    this.generatedLink = null;
    this.errorMessage = null;
    this.isGenerating = false;
    this.isCopied = false;
  };

  showModal = (): void => {
    this.resetForm();
    this.modalOpen = true;
  };

  closeModal = (): void => {
    this.modalOpen = false;
    this.resetForm();
  };

  setReason = (event: Event) => {
    this.reason = (event.target as HTMLTextAreaElement).value;
  };

  setTargetApp = (event: Event) => {
    this.targetApp = (event.target as HTMLSelectElement)
      .value as ImpersonationTargetApp;
  };

  get isXceedV2Target(): boolean {
    return this.targetApp === 'xceed-v2';
  }

  get isXceedTarget(): boolean {
    return this.targetApp === 'xceed';
  }

  generateLink = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    this.errorMessage = null;
    this.generatedLink = null;
    this.isCopied = false;

    if (!this.args.targetUserId || !this.reason.trim()) {
      this.errorMessage = 'Target user ID and reason are required.';
      return;
    }

    this.isGenerating = true;
    try {
      this.generatedLink = await createImpersonationLink(this.session, {
        targetUserId: this.args.targetUserId,
        reason: this.reason.trim(),
        targetApp: this.targetApp,
      });
    } catch (error) {
      this.errorMessage =
        error instanceof Error ? error.message : 'Failed to generate link.';
    } finally {
      this.isGenerating = false;
    }
  };

  copyUrl = async (): Promise<void> => {
    if (!this.generatedLink?.url) {
      return;
    }

    try {
      await navigator.clipboard.writeText(this.generatedLink.url);
      this.isCopied = true;
    } catch {
      window.alert('Unable to copy automatically. Select and copy the URL.');
    }
  };

  @use secondsRemaining = resource<number>(({ on }) => {
    const value = cell<number>(0);

    if (!this.generatedLink?.expiresAt) {
      value.set(0);
      return () => value.current;
    }

    const timeRemaining = () => {
      const expiresAtDate = new Date(this.generatedLink?.expiresAt ?? '');
      const now = new Date();
      const diff = expiresAtDate.getTime() - now.getTime();
      return Math.max(0, Math.floor(diff / 1000)); // Ensure we don't get a negative number
    };

    value.set(timeRemaining());

    const interval = setInterval(() => {
      value.set(timeRemaining());
    }, 1000);

    on.cleanup(() => clearInterval(interval));

    return () => value.current;
  });

  get isExpired(): boolean {
    if (!this.generatedLink?.expiresAt) {
      return false;
    }

    return this.secondsRemaining <= 0;
  }

  <template>
    {{yield this.showModal to="trigger"}}
    {{#let (uniqueId) as |uid|}}
      <BsModal
        @open={{this.modalOpen}}
        @onHidden={{this.closeModal}}
        @size="lg"
        @renderInPlace={{false}}
        @backdropClose={{true}}
        data-test-generate-link-form
        as |modal|
      >
        <modal.header>
          <h2 class="modal-title h5">Generate impersonation link</h2>
        </modal.header>
        <modal.body>

          {{#if this.errorMessage}}
            <div
              class="alert alert-danger"
              role="alert"
            >{{this.errorMessage}}</div>
          {{/if}}

          {{#if this.generatedLink}}
            <div class="mb-3">
              <input
                class="form-control font-monospace"
                type="text"
                readonly
                value={{this.generatedLink.url}}
                disabled={{this.isExpired}}
                aria-label="Impersonation link"
                data-test-impersonation-generated-url
              />
            </div>

            <div class="alert alert-info">
              Copy the link to your clipboard and open it in a private or
              incognito window. Do not use your current browser window as this
              would log you out of the admin site.
            </div>

            <div role="status">
              <p class="mb-0">
                {{#if this.isExpired}}
                  Link expired
                {{else}}
                  Link expires in
                  <strong>{{this.secondsRemaining}} seconds</strong>
                {{/if}}
              </p>
            </div>

            <div class="hstack gap-2 justify-content-end">
              <button
                type="button"
                class="btn btn-primary"
                {{on "click" this.copyUrl}}
                disabled={{this.isExpired}}
                data-test-impersonation-copy-url
              >
                <iconify-icon inline icon="ph:copy" />
                {{#if this.isCopied}}
                  Copied
                {{else}}
                  Copy link
                {{/if}}
              </button>
            </div>
          {{else}}
            <IfAbility @ability="impersonate" @atLeast="editor">
              <:if>
                <div class="mb-3">
                  <dl class="row g-1 mb-3">
                    <dt class="col-3 text-body-secondary">Target User</dt>
                    <dd class="col-9 mb-0">
                      <span
                        class="font-monospace small ms-1"
                      >{{@targetUserName}}</span>
                    </dd>

                    <dt class="col-3 text-body-secondary">User ID</dt>
                    <dd class="col-9 mb-0">
                      <span
                        class="font-monospace small ms-1"
                        data-test-impersonation-target-user-id
                      >{{@targetUserId}}</span>
                    </dd>
                  </dl>
                </div>

                <form {{on "submit" this.generateLink}}>
                  <div class="col-md-6 mb-3">
                    <label class="form-label" for="{{uid}}-target-app">
                      Target app
                    </label>
                    <select
                      id="{{uid}}-target-app"
                      class="form-select"
                      {{on "change" this.setTargetApp}}
                      {{selectValue this.targetApp}}
                      data-test-impersonation-target-app
                    >
                      {{#each TargetAppOptions as |item|}}
                        <option value={{item.value}}>{{item.label}}</option>
                      {{/each}}
                    </select>
                  </div>

                  <div class="mb-3">
                    <p>
                      Please provide a reason for impersonating this user.
                      Include an incident number if possible.
                    </p>
                    <div class="row g-3">
                      <div class="col-md-6">
                        <p class="fw-semibold mb-1 text-success">
                          Suitable reasons
                        </p>
                        <ul class="small mb-0">
                          <li>
                            Investigating a learner or staff issue in Xceed
                          </li>
                          <li>Reproducing a reported bug in Xceed</li>
                          <li>
                            Verifying permissions or content visible to the user
                          </li>
                        </ul>
                      </div>
                      <div class="col-md-6">
                        <p class="fw-semibold mb-1 text-danger">
                          Not permitted
                        </p>
                        <ul class="small mb-0">
                          <li>
                            General browsing or routine checks without an active
                            support case or incident
                          </li>
                          <li>
                            Accessing the Admin site or other internal tools
                          </li>
                          <li>
                            Any purpose unrelated to troubleshooting or incident
                            resolution
                          </li>
                        </ul>
                      </div>

                      <textarea
                        class="form-control"
                        rows="3"
                        maxlength="500"
                        value={{this.reason}}
                        {{on "input" this.setReason}}
                        data-test-impersonation-reason
                        aria-label="Reason"
                        required
                      />
                    </div>
                  </div>

                  <div class="hstack gap-2 justify-content-end">
                    <button
                      type="submit"
                      class="btn btn-primary"
                      disabled={{this.isGenerating}}
                      data-test-impersonation-generate
                    >
                      {{#if this.isGenerating}}
                        Generating…
                      {{else}}
                        Generate link
                      {{/if}}
                    </button>
                  </div>
                </form>
              </:if>
            </IfAbility>
          {{/if}}

        </modal.body>
      </BsModal>
    {{/let}}
  </template>
}
